import { APP_NAME } from '@/lib/branding'
import type { V5AddressSummary } from '@/types/api'
import type {
  CheckedDevice,
  DeviceHealth,
  Eligibility,
  Participation,
  ParticipationDevice,
  ProgramInfo,
  ParticipationStatus,
  RequirementId,
} from '@/types/programs'

/**
 * Display rules for programs. Pure: all dates and decisions come from the
 * programs API; this only turns them into Dutch labels and layout.
 */

/** Programs ship dark until a deployment sets VITE_PROGRAMS_ENABLED=true at build time. */
export const programsEnabled = import.meta.env?.VITE_PROGRAMS_ENABLED === 'true'

export const NET_BEWUST_SLUG = 'net-bewust-thuis-laden'

const PROGRAM_TIMEZONE = 'Europe/Amsterdam'

/** "1 november 2026", in the program's time zone so a local midnight stays on its own day. */
export function formatProgramDate(iso: string, timezone = PROGRAM_TIMEZONE): string {
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** A sign-up in the first days of a month starts at the moment itself, not on a later first. */
export function startsRightAway(startIso: string, now = Date.now()): boolean {
  return new Date(startIso).getTime() <= now
}

/** "1 januari": for dates whose year is obvious from the context. */
export function formatDayMonth(iso: string, timezone = PROGRAM_TIMEZONE): string {
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', timeZone: timezone }).format(
    new Date(iso)
  )
}

/** Position of the peak window on a 24-hour bar, in percent. */
export function peakWindowSegment(start: string, end: string): { leftPct: number; widthPct: number } {
  const minutes = (hhmm: string) => {
    const [h = 0, m = 0] = hhmm.split(':').map(Number)
    return h * 60 + m
  }
  const from = minutes(start)
  const to = minutes(end)
  return { leftPct: (from / 1440) * 100, widthPct: (Math.max(0, to - from) / 1440) * 100 }
}

export const REQUIREMENT_LABEL: Record<RequirementId, { title: string; hint: string }> = {
  ev: { title: 'Elektrische auto’s', hint: `Koppel je elektrische auto in de ${APP_NAME}.` },
  charger: {
    title: 'Laadpaal (optioneel)',
    hint: `Geen laadpaal gekoppeld? Je kunt toch meedoen. Heb je er een, koppel hem dan in de ${APP_NAME}.`,
  },
  p1: { title: 'Sparky of Flint', hint: 'Sparky of Flint leest je slimme meter (P1) uit.' },
}

export const HEALTH_LABEL: Record<DeviceHealth | 'unknown', string> = {
  online: 'Verbonden',
  stale: 'Even geen contact',
  offline: 'Geen verbinding',
  never: 'Nog niet verbonden',
  unknown: 'Status onbekend',
}

/** "Verbonden", or when it was last heard from if that was a while ago. */
export function deviceStatusText(device: Pick<CheckedDevice, 'health' | 'lastContact'>): string {
  if ((device.health === 'stale' || device.health === 'offline') && device.lastContact) {
    return `${HEALTH_LABEL[device.health]}: laatste contact ${formatProgramDate(device.lastContact)}`
  }
  return HEALTH_LABEL[device.health]
}

export interface ChecklistDevice {
  id: string
  name: string
  health: DeviceHealth
  status: string
}

export interface ChecklistItem {
  id: RequirementId | 'tcProgram' | 'tcDataSharing'
  ok: boolean
  /** Not needed to take part: when not ok it is shown as neutral, not as a blocker. */
  optional: boolean
  title: string
  /** How to fix it, or what to keep in mind; null when the devices say it all. */
  note: string | null
  /** Every device of this kind at the address. */
  devices: ChecklistDevice[]
  /** Connected, but some device of this kind went quiet: worth a nudge. */
  warning: boolean
}

export interface Consents {
  program: boolean
  dataSharing: boolean
}

/** The boxes to tick: a car and a P1 reader, optionally a charger, and two separate consents. */
export function buildChecklist(eligibility: Eligibility, consents: Consents): ChecklistItem[] {
  const devices: ChecklistItem[] = eligibility.checks.map((check) => {
    const label = REQUIREMENT_LABEL[check.id]
    let note: string | null = null
    if (!check.ok) note = label.hint
    else if (check.id === 'ev' && check.devices.length > 1) {
      note = 'Al je auto’s tellen mee: laad met geen van allen thuis in de piekuren.'
    }
    return {
      id: check.id,
      ok: check.ok,
      optional: !check.required,
      title: label.title,
      note,
      devices: check.devices.map((d) => ({
        id: d.id,
        name: d.name,
        health: d.health,
        // Not connected means not part of the participation; say so when others are.
        status:
          (check.ok || !check.required) && (d.health === 'offline' || d.health === 'never')
            ? `${deviceStatusText(d)} · telt niet mee`
            : deviceStatusText(d),
      })),
      warning: check.ok && check.devices.some((d) => d.health !== 'online'),
    }
  })
  return [
    ...devices,
    {
      id: 'tcProgram',
      ok: consents.program,
      optional: false,
      title: 'Voorwaarden van het programma',
      note: 'Akkoord met de deelnamevoorwaarden.',
      devices: [],
      warning: false,
    },
    {
      id: 'tcDataSharing',
      ok: consents.dataSharing,
      optional: false,
      title: 'Gegevens delen met Netbeheer Nederland',
      note: 'Akkoord met de aanvullende voorwaarden voor gegevensdeling.',
      devices: [],
      warning: false,
    },
  ]
}

// --- Mijn programma's: the participation card -------------------------------

export type Tone = 'primary' | 'success' | 'warning' | 'error' | 'muted'

/** The status pill; an opt-out in progress says when it takes effect. */
export function participationBadge(p: Participation): { label: string; tone: Tone } {
  switch (p.status) {
    case 'pending':
      return { label: 'Aangemeld', tone: 'primary' }
    case 'active':
      return { label: 'Actief', tone: 'success' }
    case 'opting_out':
      return { label: `Stopt per ${formatDayMonth(p.endDate ?? p.program.endsAt)}`, tone: 'muted' }
    case 'opted_out':
    case 'dropped':
      return { label: 'Gestopt', tone: 'error' }
    case 'cancelled':
      return { label: 'Geannuleerd', tone: 'muted' }
    case 'completed':
      return { label: 'Afgerond', tone: 'success' }
    case 'paid':
      return { label: 'Uitbetaald', tone: 'success' }
  }
}

export type TimelineState = 'done' | 'current' | 'todo' | 'stopped'

export interface TimelineStep {
  title: string
  detail: string
  state: TimelineState
}

/** Aangemeld · Deelname loopt · Einde pilot · Uitbetaling, filled up to where the participant is. */
export function participationTimeline(p: Participation): TimelineStep[] {
  const stoppedAt = p.endDate ? formatProgramDate(p.endDate) : ''
  const stopped = p.status === 'opted_out' || p.status === 'dropped' || p.status === 'cancelled'
  const current =
    p.status === 'pending' ? 0 : p.status === 'active' || p.status === 'opting_out' ? 1 : p.status === 'completed' ? 3 : 4
  const state = (i: number): TimelineState => (i < current ? 'done' : i === current ? 'current' : 'todo')
  return [
    {
      title: p.status === 'cancelled' ? 'Geannuleerd' : 'Aangemeld',
      detail: formatProgramDate(p.enrolledAt),
      state: p.status === 'cancelled' ? 'stopped' : stopped ? 'done' : state(0),
    },
    {
      title: stopped && p.status !== 'cancelled' ? `Gestopt op ${stoppedAt}` : 'Deelname loopt',
      detail: formatProgramDate(p.startDate),
      state: stopped ? (p.status === 'cancelled' ? 'todo' : 'stopped') : state(1),
    },
    { title: 'Einde pilot', detail: formatProgramDate(p.program.endsAt), state: stopped ? 'todo' : state(2) },
    {
      title: 'Uitbetaling',
      detail: p.status === 'paid' ? 'Uitbetaald' : 'Na bevestiging netbeheerder',
      state: stopped ? 'todo' : state(3),
    },
  ]
}

const ROLE_LABEL: Record<RequirementId, string> = { ev: 'Auto', charger: 'Laadpaal', p1: '' }

/** "Laadpaal Peblar", "Auto Tesla Model Y", or just "Sparky". */
export function deviceTitle(d: Pick<ParticipationDevice, 'role' | 'name'>): string {
  const name = d.name ?? (d.role === 'p1' ? 'Sparky of Flint' : 'onbekend apparaat')
  return d.role === 'p1' ? name : `${ROLE_LABEL[d.role]} ${name}`
}

const LIVE: ParticipationStatus[] = ['pending', 'active', 'opting_out']
const hasIssue = (d: ParticipationDevice) => d.health !== 'online' && d.health !== 'unknown'

export function firstDeviceIssue(p: Participation): ParticipationDevice | null {
  return LIVE.includes(p.status) ? (p.devices.find(hasIssue) ?? null) : null
}

const issueText = (d: ParticipationDevice) =>
  `${deviceTitle(d)} ${d.health === 'stale' ? 'heeft even geen contact' : 'heeft geen verbinding'}`

export interface ParticipationAlert {
  tone: Tone
  title: string
  body: string
}

/** The one message worth showing on the card, if any. A device problem comes first: it can still be fixed. */
export function participationAlert(p: Participation): ParticipationAlert | null {
  const end = formatProgramDate(p.program.endsAt)
  const issue = firstDeviceIssue(p)
  if (issue) {
    const what = issue.role === 'p1' ? issue.name ?? 'Sparky' : ROLE_LABEL[issue.role].toLowerCase()
    return {
      tone: 'warning',
      title: issueText(issue),
      body: `Herstel de verbinding in de ${APP_NAME}. Blijft de ${what} offline, dan stop je met het programma zonder beloning.`,
    }
  }
  const payer = p.payoutChannel === 'supplier' ? 'je energieleverancier' : 'Chargee'
  switch (p.status) {
    case 'pending':
      return {
        tone: 'primary',
        title: `Je start op ${formatProgramDate(p.startDate)}`,
        body: `Laad vanaf dan niet meer thuis tussen 16:00 en 21:00, en houd alles gekoppeld tot ${end}.`,
      }
    case 'opting_out':
      return {
        tone: 'muted',
        title: `Je stopt per ${formatProgramDate(p.endDate ?? p.program.endsAt)}`,
        body: 'Tot die datum blijven we je laadsessies volgen. Je ontvangt geen beloning.',
      }
    case 'opted_out':
      return {
        tone: 'muted',
        title: `Je bent gestopt per ${formatProgramDate(p.endDate ?? p.program.endsAt)}`,
        body: 'Je ontvangt geen beloning.',
      }
    case 'dropped':
      return {
        tone: 'error',
        title: 'Je doet niet meer mee',
        body: `Er is thuis geladen tijdens de piekuren of een apparaat bleef offline. Je deelname stopt per ${formatProgramDate(
          p.endDate ?? p.program.endsAt
        )} en je ontvangt geen beloning.`,
      }
    case 'cancelled':
      return {
        tone: 'muted',
        title: 'Je aanmelding is geannuleerd',
        body: 'Je kunt je opnieuw aanmelden zolang aanmelden open is.',
      }
    case 'completed':
      return {
        tone: 'success',
        title: 'De pilot is afgerond',
        body: `Je beloning wordt uitbetaald door ${payer} zodra de netbeheerder de resultaten heeft bevestigd.`,
      }
    default:
      return null
  }
}

export interface DeviceGroup {
  role: RequirementId
  label: string
  devices: ParticipationDevice[]
  issue: boolean
}

const GROUP_LABEL: Record<RequirementId, string> = { ev: "Auto's", charger: 'Laadpalen', p1: 'Sparky of Flint' }

/** Cars, chargers and the P1 reader as one setup; no charger group when none was enrolled. */
export function groupDevices(devices: ParticipationDevice[]): DeviceGroup[] {
  return (['ev', 'charger', 'p1'] as const)
    .map((role) => {
      const ofRole = devices.filter((d) => d.role === role)
      return { role, label: GROUP_LABEL[role], devices: ofRole, issue: ofRole.some(hasIssue) }
    })
    .filter((group) => group.role !== 'charger' || group.devices.length > 0)
}

/** "2 auto's · 1 laadpaal · Sparky", and whether everything is connected. */
export function setupSummary(p: Participation): { ok: boolean; title: string; counts: string } {
  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  const ev = p.devices.filter((d) => d.role === 'ev').length
  const chargers = p.devices.filter((d) => d.role === 'charger').length
  const p1 = p.devices.filter((d) => d.role === 'p1').map((d) => d.name ?? 'Sparky of Flint')
  const issue = p.devices.find(hasIssue)
  return {
    ok: !issue,
    title: issue ? issueText(issue) : 'Alles verbonden',
    counts: [count(ev, 'auto', "auto's"), ...(chargers > 0 ? [count(chargers, 'laadpaal', 'laadpalen')] : []), ...p1].join(
      ' · '
    ),
  }
}

// --- Sign-up wizard ---------------------------------------------------------

const DEVICE_KIND: Record<RequirementId, string> = { ev: 'auto', charger: 'laadpaal', p1: 'Sparky of Flint' }

/**
 * Why a sign-up was refused because a device already counts for another
 * address. Names the user's own device; never anything of another account.
 */
export function deviceInUseMessage(details: Record<string, unknown>): string {
  const kind = typeof details.kind === 'string' && details.kind in DEVICE_KIND ? (details.kind as RequirementId) : null
  const name = typeof details.deviceName === 'string' ? details.deviceName : null
  const what = name ?? (kind ? `Deze ${DEVICE_KIND[kind]}` : 'Een van je apparaten')
  return `${what} doet al mee via een ander adres. Een apparaat telt maar voor één adres mee.`
}

const MISSING_NAME: Record<RequirementId, string> = { ev: 'auto', charger: 'laadpaal', p1: 'Sparky of Flint' }

/** Why "Volgende" or "Doe mee" is disabled, or null when it is not. */
export function wizardBlocker(step: 1 | 2 | 3, eligibility: Eligibility | null, consents: Consents): string | null {
  // Missing devices are step 2's business; step 1 only stops at an address that cannot join.
  if (step === 1) return eligibility?.blockReason ? 'Met dit adres kun je niet meedoen.' : null
  if (step === 2) {
    const missing = eligibility?.checks.filter((c) => c.required && !c.ok).map((c) => MISSING_NAME[c.id]) ?? []
    if (missing.length === 0) return null
    return `Koppel eerst je ${missing.join(' en ')} om verder te gaan.`
  }
  return consents.program && consents.dataSharing ? null : 'Ga akkoord met beide voorwaarden om mee te doen.'
}

/**
 * The user's participations in one program, per address. A cancelled sign-up
 * leaves the address free again; anything else means the address has its turn.
 */
export function participationsByAddress(participations: Participation[], slug: string): Map<string, Participation> {
  return new Map(
    participations
      .filter((p) => p.program.slug === slug && p.status !== 'cancelled')
      .map((p) => [p.addressUuid, p] as const)
  )
}

// --- Who may take part ------------------------------------------------------

export type AddressIssue = 'country' | 'postcode'

/** Same rule as the API (api/src/programs/fingerprints.ts), which enforces it. */
const DUTCH_POSTCODE = /^[1-9][0-9]{3} ?(?!SA|SD|SS)[A-Z]{2}$/

/** The pilot runs in the Netherlands only, for addresses with a valid postcode. */
export function addressIssue(a: Pick<V5AddressSummary, 'country' | 'postalCode'>): AddressIssue | null {
  if ((a.country ?? '').trim().toLowerCase() !== 'nl') return 'country'
  if (!DUTCH_POSTCODE.test((a.postalCode ?? '').trim().toUpperCase())) return 'postcode'
  return null
}

export const ADDRESS_ISSUE_LABEL: Record<AddressIssue, string> = {
  country: 'Niet in Nederland',
  postcode: 'Geen geldige postcode',
}

export const ADDRESS_ISSUE_MESSAGE: Record<AddressIssue, string> = {
  country: 'Deze pilot is alleen voor adressen in Nederland.',
  postcode: `Dit adres heeft geen geldige Nederlandse postcode. Pas het adres aan in de ${APP_NAME}.`,
}

// --- Services page banner -----------------------------------------------------

export type BannerState = 'signup' | 'participating' | 'hidden'

/**
 * What the services page shows for a program: the sign-up pitch while sign-up
 * is open, a short "you take part" once the user does, nothing otherwise.
 */
export function programBannerState(program: ProgramInfo | null, mine: Map<string, Participation> | null): BannerState {
  if (!program || !mine) return 'hidden'
  const live = [...mine.values()].some((p) => p.status === 'pending' || p.status === 'active' || p.status === 'opting_out')
  if (live) return 'participating'
  return program.signup.state === 'open' ? 'signup' : 'hidden'
}

/** "Meld je aan vóór 6 november en verdien tot €20": after that date a sign-up misses a month. */
export function signupUrgency(program: ProgramInfo): string {
  return `Meld je aan vóór ${formatDayMonth(program.signup.nextRewardUntil)} en verdien tot €${program.signup.nextRewardEur}.`
}
